import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { Home, LayoutDashboard, Lock, Palette, Sparkles, User } from 'lucide-react';
import PremiumBackButton from '../components/PremiumBackButton';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { backendConfig } from '../config/backendConfig';
import SmartImage from '../components/SmartImage';
import ArtworkReactions from '../components/ArtworkReactions';
import MuseumActivityLockModal from '../components/MuseumActivityLockModal';
import PublicMuseumContributions from '../components/PublicMuseumContributions';
import { getReactionArtworkId } from '../utils/reactionArtworkId';
import {
  buildGalleryStudioUrl,
  deriveMuseumShareFromArtwork,
  enrichMuseumShare,
  loadViewerParticipationKeys,
  requiresMuseumParticipation,
  shareMatchesParticipationKeys,
  viewerParticipatedInShare,
} from '../utils/museumShareAccess';
import { getAvatarEmoji } from '../components/AvatarSelector';
import {
  resolveImageUrl as resolveImageUrlUtil,
  resolveImageUrlAsync,
} from '../utils/urlResolver';

function pickProfilePictureRaw(source) {
  if (!source || typeof source !== 'object') return null;
  const candidates = [
    source.profilePicture,
    source.photoURL,
    source.profileImage,
    source.imageURL,
    source.artistPhotoURL,
  ];
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

const PAGE_BG = {
  background: `
    radial-gradient(circle at top left, rgba(255, 209, 102, 0.18), transparent 34%),
    radial-gradient(circle at bottom right, rgba(255, 183, 77, 0.14), transparent 38%),
    linear-gradient(180deg, #FFFDF8 0%, #FFF6E8 100%)
  `,
};

const profileCacheKey = (uid) => `artmosphere:public-profile:${uid || 'anon'}`;

function readProfileCache(uid) {
  try {
    const raw = sessionStorage.getItem(profileCacheKey(uid));
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

function writeProfileCache(uid, data) {
  try {
    if (!uid || !data) return;
    sessionStorage.setItem(profileCacheKey(uid), JSON.stringify(data));
  } catch (_) {}
}

function NavPill({ icon: Icon, label, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title || label}
      className="public-gallery-nav-pill inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[rgba(245,203,92,0.55)] bg-[rgba(255,253,248,0.88)] px-3 py-1.5 text-[11px] font-semibold text-[#6B5948] shadow-[0_6px_18px_rgba(120,72,20,0.08)] backdrop-blur-md transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-[#F5CB5C] hover:text-[#2F251B] hover:shadow-[0_10px_28px_rgba(245,158,11,0.18)] sm:px-3.5 sm:py-2 sm:text-xs"
    >
      <Icon className="h-3.5 w-3.5 text-[#9A7A55]" strokeWidth={2} aria-hidden />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function StatChip({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-[rgba(245,203,92,0.45)] bg-[rgba(255,250,240,0.92)] px-3 py-1 text-[11px] font-semibold text-[#6B5948] shadow-[0_4px_12px_rgba(120,72,20,0.06)] sm:text-xs">
      {Icon ? <Icon className="h-3 w-3 text-[#F59E0B]" strokeWidth={2} aria-hidden /> : null}
      {children}
    </span>
  );
}

function PersonFallback({ className = 'h-[100px] w-[100px] md:h-[120px] md:w-[120px]' }) {
  return (
    <div
      className={`flex items-center justify-center rounded-full bg-gradient-to-br from-[#fff7e8] to-[#fdebd4] ${className}`}
    >
      <User className="h-11 w-11 text-[#c9a574] md:h-12 md:w-12" strokeWidth={1.75} aria-hidden />
    </div>
  );
}

function PublicProfileAvatar({ profile, avatarSrc, canShowPic, displayName }) {
  const sizeClass = 'h-[100px] w-[100px] md:h-[120px] md:w-[120px]';
  const photoUrl = canShowPic && avatarSrc ? avatarSrc : null;

  return (
    <div className="public-gallery-avatar group relative -mt-1 transition-all duration-300 ease-out hover:scale-[1.02]">
      <div
        className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(245,158,11,0.22),transparent_70%)] opacity-0 blur-md transition-opacity duration-300 group-hover:opacity-100"
        aria-hidden
      />
      <div className="relative rounded-full bg-gradient-to-br from-[#fcecc9] via-[#f4e0bc] to-[#e8c896] p-[3px] shadow-[0_12px_32px_rgba(160,100,40,0.18)] ring-2 ring-[rgba(255,255,255,0.95)]">
        <div className="rounded-full bg-[#fffdf8] p-0.5">
          {photoUrl ? (
            <>
              <SmartImage
                src={photoUrl}
                alt={displayName || 'Artist'}
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className={`rounded-full object-cover ${sizeClass}`}
                containerClassName={`overflow-hidden rounded-full ${sizeClass}`}
                onError={(e) => {
                  const img = e.currentTarget;
                  img.style.display = 'none';
                  const fallback = img.parentElement?.querySelector('[data-profile-fallback]');
                  if (fallback) fallback.style.display = 'flex';
                }}
              />
              <div data-profile-fallback className="hidden" style={{ display: 'none' }}>
                <PersonFallback className={sizeClass} />
              </div>
            </>
          ) : profile?.avatarId ? (
            <div
              className={`flex items-center justify-center rounded-full bg-gradient-to-br from-[#fff7e8] to-[#fdebd4] text-4xl shadow-inner md:text-5xl ${sizeClass}`}
            >
              {getAvatarEmoji(profile.avatarId)}
            </div>
          ) : (
            <PersonFallback className={sizeClass} />
          )}
        </div>
      </div>
    </div>
  );
}

function ArtworkExhibitCard({ art, index, artistUid, isLocked, onSelect }) {
  const imageSrc = resolveImageUrlUtil(art.thumbnailUrl || art.imageUrl) || art.imageUrl;
  const title = String(art.title || 'Untitled').trim();
  const reactionArtworkId = getReactionArtworkId(art);

  return (
    <article
      className="public-gallery-art-card group w-full transition-all duration-300 ease-out"
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
    >
      <div className="overflow-hidden rounded-[24px] border border-[rgba(245,203,92,0.42)] bg-[#FFFDF9] shadow-[0_14px_36px_rgba(120,72,20,0.1)] transition-all duration-300 ease-out group-hover:-translate-y-1 group-hover:shadow-[0_20px_48px_rgba(120,72,20,0.14)]">
        <button type="button" onClick={() => onSelect(art)} className="block w-full text-left">
          <div className="relative aspect-[4/3] overflow-hidden bg-[#f4efe6]">
            <div
              className="pointer-events-none absolute inset-2 z-[1] rounded-[14px] border border-white/50"
              aria-hidden
            />
            {imageSrc ? (
              <div
                className="h-full w-full overflow-hidden"
                style={
                  isLocked
                    ? {
                        filter: 'blur(20px) brightness(0.58) saturate(0.72)',
                        transform: 'scale(1.1)',
                      }
                    : undefined
                }
              >
                <SmartImage
                  src={imageSrc}
                  alt={title}
                  loading={index < 6 ? 'eager' : 'lazy'}
                  fetchPriority={index < 3 ? 'high' : 'auto'}
                  className={`h-full w-full object-cover transition-transform duration-300 ease-out ${
                    isLocked ? '' : 'group-hover:scale-[1.03]'
                  }`}
                  containerClassName="h-full w-full"
                />
              </div>
            ) : (
              <div className="flex h-full items-center justify-center text-[#c9a574]">
                <User className="h-9 w-9 opacity-50" strokeWidth={1.5} aria-hidden />
              </div>
            )}
            {isLocked ? (
              <div className="absolute inset-0 z-[2] flex flex-col items-center justify-center bg-[rgba(47,37,27,0.32)] px-4 text-center backdrop-blur-[2px]">
                <Lock className="mb-2 h-7 w-7 text-white/95 drop-shadow-sm" strokeWidth={2} aria-hidden />
                <p className="text-xs font-semibold text-white drop-shadow sm:text-sm">
                  Museum activity — contribute to unlock
                </p>
              </div>
            ) : null}
          </div>
        </button>
        <div className="px-3.5 py-3 sm:px-4 sm:py-3.5">
          <p className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[#9A7A55]">
            {isLocked ? 'Museum Artwork' : 'Public Artwork'}
          </p>
          <button
            type="button"
            onClick={() => onSelect(art)}
            className="mt-0.5 block w-full truncate text-left text-sm font-semibold text-[#2F251B] transition hover:text-[#F59E0B] sm:text-[15px]"
          >
            {title}
          </button>
          {!isLocked ? (
            <div
              className="mt-2 flex justify-end"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              <ArtworkReactions
                artworkId={reactionArtworkId}
                artistId={artistUid || art.uid}
                enableComments={false}
                compact
              />
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
}

function GrowthPlaceholder() {
  return (
    <div
      className="public-gallery-art-card flex min-h-[220px] flex-col justify-center rounded-[24px] border border-dashed border-[rgba(245,203,92,0.45)] bg-[rgba(255,253,248,0.65)] px-6 py-8 text-center shadow-[0_8px_24px_rgba(120,72,20,0.05)] sm:min-h-[280px]"
      style={{ animationDelay: '120ms' }}
    >
      <Sparkles className="mx-auto mb-3 h-7 w-7 text-[#F59E0B]/80" strokeWidth={1.75} aria-hidden />
      <h3 className="text-base font-semibold text-[#2F251B] sm:text-lg">More artworks may appear soon</h3>
      <p className="mx-auto mt-2 max-w-[240px] text-sm leading-relaxed text-[#6B5948]">
        Every shared creation helps this gallery grow.
      </p>
    </div>
  );
}

export default function ArtistPublicGallery() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const { user: viewer } = useAuth();
  const viewerUid = viewer?.uid || null;
  const cached = useMemo(() => readProfileCache(uid), [uid]);

  const [artistName, setArtistName] = useState(cached?.displayName || '');
  const [artistProfile, setArtistProfile] = useState(cached?.profile || null);
  const [canDisplayProfilePic, setCanDisplayProfilePic] = useState(
    cached?.canShowPic !== false
  );
  const [artworks, setArtworks] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [lockModalShare, setLockModalShare] = useState(null);
  const [participationKeys, setParticipationKeys] = useState(() => new Set());
  const [profileReady, setProfileReady] = useState(!!cached?.displayName);
  const [avatarSrc, setAvatarSrc] = useState(() => {
    if (cached?.canShowPic === false) return '';
    const raw = pickProfilePictureRaw(cached?.profile);
    return raw ? resolveImageUrlUtil(raw) || raw : '';
  });

  useEffect(() => {
    if (!canDisplayProfilePic) {
      setAvatarSrc('');
      return undefined;
    }
    const raw = pickProfilePictureRaw(artistProfile);
    if (!raw) {
      setAvatarSrc('');
      return undefined;
    }
    let cancelled = false;
    const sync = resolveImageUrlUtil(raw) || raw;
    setAvatarSrc(sync);
    resolveImageUrlAsync(raw).then((resolved) => {
      if (!cancelled && resolved) setAvatarSrc(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [artistProfile, canDisplayProfilePic]);

  useEffect(() => {
    if (avatarSrc || !canDisplayProfilePic || !artworks.length) return undefined;
    const raw = artworks.map((art) => art.artistPhotoURL).find((url) => typeof url === 'string' && url.trim());
    if (!raw) return undefined;
    let cancelled = false;
    const sync = resolveImageUrlUtil(raw) || raw;
    setAvatarSrc(sync);
    resolveImageUrlAsync(raw).then((resolved) => {
      if (!cancelled && resolved) setAvatarSrc(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, [artworks, avatarSrc, canDisplayProfilePic]);

  useEffect(() => {
    if (!uid) return;

    let cancelled = false;

    const applyProfilePayload = (displayName, profile, canShowPic) => {
      if (cancelled) return;
      setArtistProfile(profile);
      setArtistName(displayName);
      setCanDisplayProfilePic(canShowPic);
      setProfileReady(true);
      writeProfileCache(uid, { displayName, profile, canShowPic });
    };

    const fetchArtistProfileFromFirestore = async () => {
      let artist = null;
      const profileById = await getDoc(doc(db, 'artistProfiles', uid));
      if (profileById.exists()) {
        artist = profileById.data();
      } else {
        const profileSnap = await getDocs(
          query(collection(db, 'artistProfiles'), where('uid', '==', uid))
        );
        if (!profileSnap.empty) artist = profileSnap.docs[0].data();
      }

      let displayName = artist?.nickname || artist?.name || '';
      let canShowPic = true;

      if (artist) {
        try {
          const response = await fetch(`${backendConfig.baseUrl}/api/consent/public-display/${uid}`);
          if (response.ok) {
            const data = await response.json();
            const canDisplayName = data.canDisplayName !== false;
            canShowPic = data.canDisplayProfilePic !== false;
            if (canDisplayName) {
              displayName = artist.name || artist.nickname || displayName;
            } else {
              displayName = artist.nickname || artist.name || displayName;
            }
          }
        } catch (_) {
          displayName = artist.name || artist.nickname || displayName;
        }
      }

      if (!displayName) {
        const artSnap = await getDocs(
          query(
            collection(db, 'artworks'),
            where('uid', '==', uid),
            where('visibility', '==', 'public')
          )
        );
        for (const artDoc of artSnap.docs) {
          const data = artDoc.data();
          const candidate = data.studentName || data.artistName || data.displayName;
          if (typeof candidate === 'string' && candidate.trim()) {
            displayName = candidate.trim();
            break;
          }
        }
      }

      if (!displayName) displayName = 'Artist';

      const rawPic = pickProfilePictureRaw(artist);
      const profile = artist
        ? {
            ...artist,
            profilePicture: rawPic,
          }
        : null;

      applyProfilePayload(displayName, profile, canShowPic);
    };

    const fetchArtistProfile = async () => {
      try {
        const response = await fetch(
          `${backendConfig.baseUrl}/api/consent/public-artist-profile/${uid}`
        );
        if (response.ok) {
          const data = await response.json();
          const displayName = data.displayName || 'Artist';
          const canShowPic = data.canDisplayProfilePic !== false;
          const rawPic =
            pickProfilePictureRaw(data) ||
            pickProfilePictureRaw(data.profile) ||
            data.profilePicture ||
            null;
          const profile = data.profile
            ? {
                ...data.profile,
                bio: data.bio || data.profile.bio || '',
                avatarId: data.avatarId ?? data.profile.avatarId ?? null,
                profilePicture: rawPic,
              }
            : data.avatarId || data.bio || rawPic
              ? {
                  bio: data.bio || '',
                  avatarId: data.avatarId || null,
                  profilePicture: rawPic,
                }
              : null;

          applyProfilePayload(displayName, profile, canShowPic);
          return;
        }
      } catch (err) {
        console.warn('[ArtistPublicGallery] API profile load failed, using Firestore:', err);
      }

      try {
        await fetchArtistProfileFromFirestore();
      } catch (err) {
        console.warn('[ArtistPublicGallery] profile load failed:', err);
        if (!cancelled && !artistName) {
          setArtistName('Artist');
          setProfileReady(true);
        }
      }
    };

    const fetchArtworks = async () => {
      try {
        const snapshot = await getDocs(
          query(collection(db, 'artworks'), where('uid', '==', uid), where('visibility', '==', 'public'))
        );
        if (!cancelled) {
          setArtworks(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
        }
      } catch (err) {
        console.warn('[ArtistPublicGallery] artworks load failed:', err);
        if (!cancelled) setArtworks([]);
      }
    };

    fetchArtistProfile();
    fetchArtworks();

    return () => {
      cancelled = true;
    };
  }, [uid]);

  useEffect(() => {
    if (!viewerUid) {
      setParticipationKeys(new Set());
      return undefined;
    }

    let cancelled = false;
    loadViewerParticipationKeys(viewerUid).then((keys) => {
      if (!cancelled) setParticipationKeys(keys);
    });

    return () => {
      cancelled = true;
    };
  }, [viewerUid]);

  const isViewingOwnProfile = Boolean(viewerUid && uid && viewerUid === uid);

  const viewerCanAccessShare = useCallback(
    (share) => {
      const enriched = enrichMuseumShare(share);
      if (!enriched) return true;
      if (!requiresMuseumParticipation(enriched)) return true;
      if (isViewingOwnProfile) return true;
      if (!viewerUid) return false;
      if (shareMatchesParticipationKeys(enriched, participationKeys)) return true;
      return false;
    },
    [isViewingOwnProfile, viewerUid, participationKeys]
  );

  const handleMuseumShareAccess = useCallback(
    async (share) => {
      const enriched = enrichMuseumShare(share);
      if (!enriched) return;

      if (isViewingOwnProfile) {
        navigate(buildGalleryStudioUrl(enriched));
        return;
      }

      if (viewerUid && shareMatchesParticipationKeys(enriched, participationKeys)) {
        navigate(buildGalleryStudioUrl(enriched));
        return;
      }

      if (viewerUid) {
        const participated = await viewerParticipatedInShare(enriched, viewerUid);
        if (participated) {
          navigate(buildGalleryStudioUrl(enriched));
          return;
        }
      }

      setLockModalShare(enriched);
    },
    [isViewingOwnProfile, navigate, participationKeys, viewerUid]
  );

  const isArtworkLockedForViewer = useCallback(
    (art) => {
      if (isViewingOwnProfile) return false;
      const museumShare = deriveMuseumShareFromArtwork(art);
      if (!museumShare || !requiresMuseumParticipation(museumShare)) return false;
      return !viewerCanAccessShare(museumShare);
    },
    [isViewingOwnProfile, viewerCanAccessShare]
  );

  const handleArtworkSelect = useCallback(
    async (art) => {
      const museumShare = deriveMuseumShareFromArtwork(art);
      if (museumShare && !viewerCanAccessShare(museumShare)) {
        if (viewerUid) {
          const participated = await viewerParticipatedInShare(
            enrichMuseumShare(museumShare),
            viewerUid
          );
          if (participated) {
            setSelectedPost(art);
            return;
          }
        }
        setLockModalShare(enrichMuseumShare(museumShare));
        return;
      }
      setSelectedPost(art);
    },
    [viewerCanAccessShare, viewerUid]
  );

  const nameLabel = artistName || (profileReady ? 'Artist' : '');
  const artworkCountLabel = `${artworks.length} Public Artwork${artworks.length === 1 ? '' : 's'}`;
  const showGrowthPlaceholder = artworks.length === 1;

  return (
    <div className="public-gallery-page relative min-h-screen" style={PAGE_BG}>
      <style>{`
        @keyframes publicGalleryFadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .public-gallery-fade-in {
          animation: publicGalleryFadeIn 0.55s ease-out both;
        }
        .public-gallery-art-card {
          animation: publicGalleryFadeIn 0.55s ease-out both;
        }
      `}</style>

      <div className="relative z-10 mx-auto max-w-[1200px] px-4 py-5 sm:px-5 sm:py-6 md:px-6 md:py-7">
        {/* Top bar: back + navigation */}
        <div className="public-gallery-fade-in mb-5 flex flex-col gap-3 sm:mb-6">
          <div className="flex items-start justify-between gap-3">
            <PremiumBackButton variant="icon" to={-1} title="Go back" />

            <div className="flex max-w-[calc(100%-3.5rem)] flex-nowrap items-center justify-end gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-2 [&::-webkit-scrollbar]:hidden md:max-w-none">
              <NavPill
                icon={LayoutDashboard}
                label="Artist Corner"
                onClick={() => navigate('/my-art-world')}
              />
              <NavPill icon={Palette} label="My Gallery" onClick={() => navigate('/artist-wall')} />
              <NavPill icon={Home} label="Home" onClick={() => navigate('/home')} />
            </div>
          </div>
        </div>

        {/* Main profile card */}
        <div
          className="public-gallery-fade-in relative overflow-hidden rounded-[28px] border border-[rgba(245,203,92,0.45)] bg-[rgba(255,255,255,0.78)] shadow-[0_24px_70px_rgba(120,72,20,0.12)] backdrop-blur-[18px] sm:rounded-[36px]"
          style={{ animationDelay: '80ms' }}
        >
          <div
            className="h-1.5 w-full"
            style={{
              background: 'linear-gradient(90deg, rgba(255,196,87,0.16), transparent)',
            }}
            aria-hidden
          />

          {/* Profile (left) + Artist Salon (right) on md+ */}
          <div className="relative border-b border-[rgba(245,203,92,0.28)] md:flex md:flex-row md:items-stretch">
            <div className="relative min-w-0 flex-1 px-5 pb-6 pt-7 text-center sm:px-8 sm:pb-7 sm:pt-8 md:px-8 md:pb-8 md:pt-9 md:text-left lg:px-10">
              <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-[radial-gradient(ellipse_at_top,rgba(255,209,102,0.12),transparent_70%)] md:left-0 md:right-auto md:w-2/3" aria-hidden />

              <div className="relative flex flex-col items-center md:items-start">
                <PublicProfileAvatar
                  profile={artistProfile}
                  avatarSrc={avatarSrc}
                  canShowPic={canDisplayProfilePic}
                  displayName={nameLabel}
                />

                <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9A7A55] sm:text-[11px]">
                  Public Artist Gallery
                </p>

                <h1 className="mt-2 text-[clamp(1.5rem,4vw,2rem)] font-bold leading-tight text-[#2F251B] md:text-[32px]">
                  {profileReady ? (
                    nameLabel
                  ) : (
                    <span className="mx-auto inline-block h-8 w-40 animate-pulse rounded-lg bg-[#efe2d0]/80 md:mx-0" />
                  )}
                </h1>

                {artistProfile?.bio ? (
                  <p className="mx-auto mt-3 max-w-[520px] text-sm leading-relaxed text-[#6B5948] sm:text-[15px] md:mx-0 md:text-base">
                    {artistProfile.bio}
                  </p>
                ) : null}

                <div className="mt-4 flex flex-wrap items-center justify-center gap-2 md:justify-start">
                  <StatChip icon={Palette}>{artworkCountLabel}</StatChip>
                </div>
              </div>
            </div>

            <PublicMuseumContributions
              uid={uid}
              layout="split"
              onShareClick={handleMuseumShareAccess}
              canAccessShare={viewerCanAccessShare}
            />
          </div>

          {/* Gallery collection */}
          <div className="border-t border-[rgba(245,203,92,0.28)] bg-[rgba(255,253,248,0.45)] px-5 py-6 sm:px-8 sm:py-7 md:px-10 md:py-8">
            <div className="mb-6 md:mb-7">
              <h2 className="text-lg font-bold text-[#2F251B] sm:text-xl">Gallery Collection</h2>
              <p className="mt-1 max-w-xl text-sm text-[#6B5948] sm:text-[15px]">
                Explore artworks this young artist has chosen to share publicly.
              </p>
            </div>

            {artworks.length === 0 ? (
              <div className="rounded-[24px] border border-dashed border-[rgba(245,203,92,0.45)] bg-[rgba(255,253,248,0.7)] px-6 py-12 text-center">
                <Palette className="mx-auto mb-3 h-8 w-8 text-[#F59E0B]/70" strokeWidth={1.75} aria-hidden />
                <p className="text-sm font-semibold text-[#2F251B]">No public artworks yet.</p>
                <p className="mt-1 text-sm text-[#6B5948]">Check back soon as this gallery grows.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 sm:gap-8 lg:grid-cols-3">
                {artworks.map((art, index) => (
                  <ArtworkExhibitCard
                    key={art.id}
                    art={art}
                    index={index}
                    artistUid={uid}
                    isLocked={isArtworkLockedForViewer(art)}
                    onSelect={handleArtworkSelect}
                  />
                ))}
                {showGrowthPlaceholder ? <GrowthPlaceholder /> : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <MuseumActivityLockModal share={lockModalShare} onClose={() => setLockModalShare(null)} />

      {selectedPost && !isArtworkLockedForViewer(selectedPost) && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-4"
          onClick={() => setSelectedPost(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-[#e8d4bc]/80 bg-[#fffdf9] shadow-2xl lg:flex-row"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative min-h-[40vh] flex-1 bg-[#f4efe6] lg:min-h-0">
              <SmartImage
                src={resolveImageUrlUtil(selectedPost.imageUrl) || selectedPost.imageUrl}
                alt={selectedPost.title}
                loading="eager"
                fetchPriority="high"
                className="h-full max-h-[50vh] w-full object-contain lg:max-h-[92vh]"
                containerClassName="flex h-full min-h-[240px] w-full items-center justify-center"
              />
            </div>
            <div className="relative w-full shrink-0 overflow-y-auto p-4 sm:p-5 lg:w-80">
              <button
                type="button"
                onClick={() => setSelectedPost(null)}
                className="absolute right-3 top-3 rounded-full border border-[#e8d4bc] bg-white/90 px-2 py-1 text-lg leading-none text-[#5A4632] hover:bg-white"
                aria-label="Close"
              >
                ×
              </button>
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-[#8A7357]">Artwork</p>
              <h2 className="mt-1 pr-8 text-lg font-semibold text-[#2F251B]">
                {selectedPost.title || 'Untitled'}
              </h2>
              {selectedPost.description ? (
                <p className="mt-3 text-sm leading-relaxed text-[#5A4632]">{selectedPost.description}</p>
              ) : null}
              <dl className="mt-4 space-y-2 text-sm text-[#5A4632]">
                {selectedPost.technique ? (
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#8A7357]">
                      Technique
                    </dt>
                    <dd>{selectedPost.technique}</dd>
                  </div>
                ) : null}
                {selectedPost.inspiration ? (
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#8A7357]">
                      Inspiration
                    </dt>
                    <dd>{selectedPost.inspiration}</dd>
                  </div>
                ) : null}
                {selectedPost.tag ? (
                  <div>
                    <dt className="text-[10px] font-semibold uppercase tracking-wide text-[#8A7357]">
                      Collection
                    </dt>
                    <dd>{selectedPost.tag}</dd>
                  </div>
                ) : null}
              </dl>
              <p className="mt-4 text-xs text-[#8A7357]">
                Artist: <span className="font-semibold text-[#2F251B]">{nameLabel}</span>
              </p>
              <div
                className="mt-4 border-t border-[#efe2d0]/80 pt-4"
                onClick={(e) => e.stopPropagation()}
                role="presentation"
              >
                <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8A7357]">
                  Cheer for this artwork
                </p>
                <ArtworkReactions
                  artworkId={getReactionArtworkId(selectedPost)}
                  artistId={uid}
                  enableComments={false}
                  compact
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
